import { ClinicAuthError } from "@auth/clinicAuthClient";
import { useSessionStore } from "@auth/sessionStore";
import { notificationsKeys } from "@notifications/notificationsQueries";
import { type CardSeverity, maxSeverity } from "@subjects/cardSeverity";
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { tasksKeys } from "@tasks/tasksQueries";
import { viewsClient } from "@views/viewsClient";

export const viewsKeys = {
  list: (sessionKey: string, subject: string | null) =>
    ["views", sessionKey, subject] as const,
  surfaced: (sessionKey: string, subject: string) =>
    ["views", sessionKey, subject, "surfaced"] as const,
  detail: (id: string) => ["view", id] as const,
  /** Its own root, deliberately outside the `["views"]` prefix: this is a
   * historical read, and the fan-out must not refetch on every view mutation
   * and socket `view` frame. */
  attention: (entityId: string) => ["view-attention", entityId] as const,
  tasks: (id: string) => ["view", id, "tasks"] as const,
  signoffs: (id: string) => ["view", id, "signoffs"] as const,
};

export const viewsListQueryOptions = (
  sessionKey: string,
  subject: string | null,
) => ({
  queryKey: viewsKeys.list(sessionKey, subject),
  queryFn: () => viewsClient.list(subject ?? undefined),
});

export const useViewsList = (sessionKey: string, subject: string | null) =>
  useSuspenseQuery(viewsListQueryOptions(sessionKey, subject));

/** Shared options for a subject's surfaced views, so the suspense slot and the
 * batch severity read below hit the exact same cache entry (same key + fetcher).
 * The long staleTime keeps a busy board from re-issuing one request per
 * occupant on every remount (the old briefing cadence); freshness comes from
 * the socket's `view` frames invalidating the `"views"` prefix, not polling. */
export const surfacedViewsQueryOptions = (
  sessionKey: string,
  subject: string,
) => ({
  queryKey: viewsKeys.surfaced(sessionKey, subject),
  queryFn: () => viewsClient.surfaced(subject),
  staleTime: 5 * 60_000,
});

/**
 * The subject's agent-surfaced views, in surfaced order — what the dashboard
 * views slot shows (WEB-3189). Shares the `["views", …]` prefix so the delete
 * mutation's invalidation catches it too.
 */
export const useSurfacedViews = (sessionKey: string, subject: string) =>
  useSuspenseQuery(surfacedViewsQueryOptions(sessionKey, subject));

/**
 * Batch, non-suspense read of several patients' card severity, keyed by patient id — the
 * max severity across each patient's surfaced views (WEB-3214, WEB-3251). Used to tint
 * room tiles and in-room occupant cards by the occupant's most urgent surfaced view.
 *
 * Non-suspense so a still-loading subject never blanks the board (it reads `calm` until
 * its views resolve). Shares `surfacedViewsQueryOptions`, so fetching here also warms the
 * cache the subject dashboard reads when the room/patient is opened.
 */
export const usePatientSeverities = (
  patientIds: string[],
): Map<string, CardSeverity> => {
  const sessionKey = useSessionStore((s) => s.user?.userUuid ?? "");
  const results = useQueries({
    queries: patientIds.map((id) => surfacedViewsQueryOptions(sessionKey, id)),
  });
  return new Map(
    patientIds.map((id, i) => [
      id,
      maxSeverity((results[i].data ?? []).map((view) => view.severity)),
    ]),
  );
};

export const useViewDetail = (id: string) =>
  useSuspenseQuery({
    queryKey: viewsKeys.detail(id),
    queryFn: () => viewsClient.get(id),
  });

/**
 * The clinic's views for the command palette — a plain (non-suspense) query so
 * the search modal can fold them into its results without its own boundary.
 * Shares the clinic-wide list cache key.
 */
export const useClinicViews = (sessionKey: string, enabled: boolean) =>
  useQuery({
    queryKey: viewsKeys.list(sessionKey, null),
    queryFn: () => viewsClient.list(),
    enabled,
  });

export const useDeleteView = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: viewsClient.remove,
    meta: { action: "delete the view" },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["views"] }),
  });
};

/**
 * Sign a view (BE-2034). Refreshes the view lists, any open view detail
 * (`["view", id]`), the notifications feed, and the clinic-wide task queries
 * (`["tasks"]`) — signing auto-closes the sign task, so its count and lists must
 * drop it, and it flips the view's lifecycle badge.
 *
 * `action` names what the caller calls this, since the same endpoint backs both
 * "sign the view" (SignTaskCard) and "approve the estimate" (EstimateCard), and
 * a failure notice has to use the word the user just read on the button.
 *
 * The 409 refresh and the global write-error notice deliberately both fire: the
 * refresh flips the card to signed, and the notice says why. Without it the card
 * changing under you reads as your own click succeeding, when someone else's did
 * and their name is on the sign-off.
 */
export const useSignView = (action = "sign the view") => {
  const queryClient = useQueryClient();
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["views"] }),
      queryClient.invalidateQueries({ queryKey: ["view"] }),
      queryClient.invalidateQueries({ queryKey: notificationsKeys.all }),
      queryClient.invalidateQueries({ queryKey: tasksKeys.all }),
    ]);
  return useMutation({
    mutationFn: viewsClient.sign,
    meta: { action },
    onSuccess: refresh,
    onError: (error) => {
      if (error instanceof ClinicAuthError && error.status === 409) refresh();
    },
  });
};

/**
 * Remove one estimate line item (BE-2035). The endpoint returns the updated
 * view, so we prime its detail cache and invalidate the view lists (dashboard
 * slot / browser) to re-render without the row.
 */
export const useRemoveViewItem = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ viewId, itemId }: { viewId: string; itemId: string }) =>
      viewsClient.removeItem(viewId, itemId),
    meta: { action: "remove the item" },
    onSuccess: (view) => {
      queryClient.setQueryData(viewsKeys.detail(view.id), view);
      return queryClient.invalidateQueries({ queryKey: ["views"] });
    },
  });
};

/**
 * Ask the agent to rework an open view's items (BE-2035). Fire-and-forget: the
 * reworked view arrives via the usual socket update, so there's nothing to
 * invalidate here — callers show their own submit feedback.
 */
export const useRequestViewChanges = () =>
  useMutation({
    mutationFn: ({ viewId, note }: { viewId: string; note: string }) =>
      viewsClient.requestChanges(viewId, note),
    meta: { action: "send your request" },
  });

/**
 * The authoritative open tasks pointing at one view (WEB-3247), via the
 * `task_views` join (BE-2034). Fetched only for the opened-view bell — one
 * request, always the complete set (unlike the notifications-feed count the
 * compact card badge uses, which is capped). Invalidated by sign/unlock and any
 * task action (see `useTaskActions`), both under the `["view"]` prefix. Signing
 * a view invalidates this too.
 */
export const useViewTasks = (id: string) =>
  useQuery({
    queryKey: viewsKeys.tasks(id),
    queryFn: () => viewsClient.tasks(id),
  });

/**
 * A view's sign-off audit trail (BE-2034), for the admin Signoffs panel. Each
 * signed view carries one `signed` event; non-suspense so a slow row can't stall
 * the list.
 */
export const useViewSignoffs = (id: string) =>
  useQuery({
    queryKey: viewsKeys.signoffs(id),
    queryFn: () => viewsClient.signoffs(id),
  });
